import {
  createElement,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { useDispatchApp, useSelectorApp } from "../../../../lib/redux";
import { startGetAdPreview } from "../../../../actions/adsCampaigns";
import { Controller, useForm, useFormContext } from "react-hook-form";
import {
  formattedIframeByType,
  getIsAdWithSpecialConfigurationCustomizationPlacements,
  getIsCarouselAdIsAvailable,
} from "../../../../services/adsCampaigns";
import {
  changeCreativeWithOptionalTextRegeneration,
  getMatchedCreativeEntryForAdSlot,
} from "../../../../services/creativeChangeWithTextRegeneration";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_STANDARD,
  ADS_CAMPAIGN_CAROUSEL_FORMAT,
  ADS_CAMPAIGN_GET_DIRECTIONS_CALL_TO_ACTION_OPTION,
  ADS_CAMPAIGN_META_AD_FORMATS_FILTERED_BY_SPECIAL_CONFIGURATION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_INSTAGRAM_PROFILE_OPTION,
  ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_VIDEO_OPTION,
  ADS_CAMPAIGN_META_OPTIMIZATION_GOAL_THRUPLAY,
  ADS_CAMPAIGN_META_PARTNERSHIP_AD_ORDER_SELF_FIRST,
  ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION,
  DELAY_DEBOUNCE,
  FACEBOOK,
  FORMATS_MEDIA_META,
  META,
  MIN_DIMENSIONS_CONTENT,
  VIDEO_FORMATS,
} from "../../../../utils/constants";
import {
  ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_MOBILE_FEED_STANDARD,
  ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_REELS_MOBILE,
  ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_STORY,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_REEL,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_STORY,
  BUTTON_EDIT_CONTENT,
  BUTTON_RETRY,
  BUTTON_SELECT_CONTENT,
  BUTTON_SELECT_POST,
  FIELD_AD_FORMAT_REQUIRED_AD_PREVIEW,
  META_AD_PREVIEW_AD_BLOCKER_NOTICE,
  FIELD_CONTENT_IS_BEING_PROCESSED,
  FIELD_REQUIRED,
  FORMAT_PLACEHOLDER,
  BUTTON_REPLACE_CONTENT,
} from "../../../../i18n/keysTranslations";
import Select from "../../../../components/Form/Select";
import Typography from "@mui/material/Typography";
import IconButtonAddContent from "../../../../components/Buttons/IconButtonAddContent";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import { useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import _ from "lodash";
import MetaContainerIntegration from "../../../../components/Meta/MetaContainerIntegration";
import CardMedia from "../../../../components/Media/CardMedia";
import {
  getExtensionFile,
  verifyDestinationURLIsABlockedDomain,
} from "../../../../utils/string";
import {
  getIsCatalogAd,
  getIsOnPostDestinationMeta,
  validationMetaAdPreview,
} from "../../../../services/meta";
import Button from "../../../../components/Buttons/Button";
import RefreshIcon from "@mui/icons-material/Refresh";
import TooltipOptimization from "../../../Tooltip/TooltipOptimization";
import AdPreviewRegeneratingTextsOverlay from "../../AdPreviewRegeneratingTextsOverlay";
import { buildCreativeOptimizationPrompt } from "../../../../utils/creativeOptimizationContext";
import ModalImageEdit from "../../../../components/Modal/ModalImageEdit";
import { filterFormatsByManualPlacements } from "../../../../utils/catalogs/metaPlacementAdFormats";
import { applyMetaReusableCreativeToAd } from "../../../../utils/metaCreativeReuse";

const FORMATS_CAROUSEL = [
  ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_MOBILE_FEED_STANDARD,
  ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_REELS_MOBILE,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_STORY,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_STANDARD,
];
const FORMATS_INSTAGRAM = [
  ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_STORY,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_STORY,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_REEL,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_STANDARD,
];
const FORMATS_SINGLE_META_WITH_GET_DIRECTIONS = [
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_REEL,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_STANDARD,
  ADS_CAMPAIGN_AD_FORMAT_INSTAGRAM_STORY,
  ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_MOBILE_FEED_STANDARD,
];

const getAvailableFormats = ({ isCarousel, callToAction }) => {
  if (isCarousel) {
    return FORMATS_CAROUSEL;
  }
  if (callToAction === ADS_CAMPAIGN_GET_DIRECTIONS_CALL_TO_ACTION_OPTION) {
    return FORMATS_SINGLE_META_WITH_GET_DIRECTIONS;
  }
  return FORMATS_INSTAGRAM;
};

const serializeCopyFieldsSignature = ({
  titles = [],
  headlines = [],
  descriptions = [],
}) =>
  [titles, headlines, descriptions]
    .map((items) =>
      (items || []).map((item) => item?.value ?? "").join("\u0001")
    )
    .join("\u0002");

const getConfigurationPreviewType = ({ mediaUrls = [], platformData }) => {
  const showAdPreview = Boolean(
    platformData?.adAccountID && platformData?.integrationID
  );

  const showMediaUpload = Boolean(!showAdPreview && mediaUrls?.length > 0);

  return {
    showAdPreview,
    showMediaUpload,
  };
};

const AdPreviewMeta = forwardRef((props, ref) => {
  const {
    adID,
    mediaUrls = [],
    mediaData = [],
    basicPath,
    basicPathAdGroup,
  } = props;
  const [currentMediaIndex] = useState(0);
  const [formats, setFormats] = useState([]);
  const [adPreviewSrc, setAdPreviewSrc] = useState("");
  const [triggerGeneratePreview, setTriggerGeneratePreview] = useState(0);
  const [isRegeneratingTexts, setIsRegeneratingTexts] = useState(false);
  const [isLoadingIframe, setIsLoadingIframe] = useState(true);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  const showIframePreviewLoader = isLoadingIframe && !isRegeneratingTexts;

  const currentMediaUrl = mediaUrls[currentMediaIndex];
  const currentMediaData = mediaData[currentMediaIndex];
  const customizationPlacements =
    currentMediaData?.customizationPlacements || [];

  const {
    isGeneralDisabledFields,
    campaignID,
    watch,
    setValue,
    getValues,
    previewsCached,
    onChangePreviewsCached,
  } = useFormContext();

  const platformData = watch(`platformsData.${META}`) || {};
  const isIOS14Campaign = platformData?.isIOS14Campaign;
  const isIncompatibleCampaign = platformData?.isIncompatible;

  const isIncompatibleAdGroup =
    getValues(`${basicPathAdGroup}.isIncompatible`) || false;

  const isCarousel = watch(`${basicPath}.isCarousel`);
  const isDynamicCreative =
    watch(`${basicPathAdGroup}.isDynamicCreative`) || false;
  const isInstantExperience =
    watch(`${basicPath}.isInstantExperience`) || false;
  const address = watch(`${basicPath}.address`) || "";
  const isIncompatibleAd = getValues(`${basicPath}.isIncompatible`) || false;
  const callToActionFromForm = watch(`${basicPath}.callToAction`) || "";
  const callToActionsFromForm = watch(`${basicPath}.callToActions`) || [];
  const callToAction = isDynamicCreative
    ? callToActionsFromForm?.[0] || ""
    : callToActionFromForm;
  const instantExperience = watch(`${basicPath}.instantExperience`) || null;

  const isDisabledFields =
    isGeneralDisabledFields ||
    isIncompatibleCampaign ||
    isIncompatibleAdGroup ||
    isIncompatibleAd;

  const isPreviewFormatSelectDisabled =
    isGeneralDisabledFields &&
    !isIncompatibleCampaign &&
    !isIncompatibleAdGroup &&
    !isIncompatibleAd;

  const extension = getExtensionFile(currentMediaUrl);
  const isVideo = VIDEO_FORMATS.includes(extension);

  const {
    control,
    errors,
    setValue: setValueComponent,
    watch: watchComponent,
  } = useForm({
    defaultValues: {
      adFormat: formats[0],
    },
  });
  const errorMediaUrl = getReactHookFormNestedError(
    errors,
    `${basicPath}.mediaUrls`
  );
  const adFormat = watchComponent("adFormat");

  const destinationTypeAdGroup = watch(`${basicPathAdGroup}.destinationType`);
  const catalogIDAdGroup = watch(`${basicPathAdGroup}.catalogID`);
  const appID = watch(`${basicPathAdGroup}.appID`);
  const groupPlacements = watch(`${basicPathAdGroup}.placements`);
  const destinationTypeAd = watch(`${basicPath}.destinationType`);
  const siteLinks = watch(`${basicPath}.siteLinks`);
  const platforms = watch("platforms");
  const websiteURL = watch("websiteURL");
  const language = watch("language");
  const objective = platformData?.objective;
  const mediaIDs = watch(`${basicPath}.mediaIDs`);
  const destinationUrls = watch(`${basicPath}.destinationUrls`) || [];
  const creativeSource = watch(`${basicPath}.creativeSource`) || [];
  const productSetID = watch(`${basicPath}.productSetID`);
  const id = watch(`${basicPath}.id`);
  const formTitlesWatch = watch(`${basicPath}.titles`);
  const formHeadlinesWatch = watch(`${basicPath}.headlines`);
  const formDescriptionsWatch = watch(`${basicPath}.descriptions`);
  const copyFieldsSignature = serializeCopyFieldsSignature({
    titles: formTitlesWatch,
    headlines: formHeadlinesWatch,
    descriptions: formDescriptionsWatch,
  });

  const isEngagementAndOnVideo =
    objective === ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION &&
    destinationTypeAdGroup ===
      ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_VIDEO_OPTION;

  const dispatch = useDispatchApp();

  const metaIntegrations = useSelectorApp(
    (state) => state?.user?.integrations?.[FACEBOOK]
  );
  const { t } = useTranslationApp();
  const isMobile = useMediaQuery(() => "(max-width: 360px)");
  const noHasPlatformData = _.isEmpty(metaIntegrations);

  const isCatalogAd = getIsCatalogAd({
    platformData,
    objective,
    catalogIDAdGroup,
    creativeSource,
  });

  const { showAdPreview, showMediaUpload } = getConfigurationPreviewType({
    mediaData,
    mediaUrls,
    platformData,
    productSetID,
  });

  const { userID } = dispatch(getPathDatabaseByRole());

  const pageID = platformData?.pageID;
  const instagramID = platformData?.instagramID;
  const useFBPageAsIG = platformData?.useFBPageAsIG || false;

  const isPostAd = watch(`${basicPath}.isPostAd`) || false;
  const isPartnershipAd = watch(`${basicPath}.isPartnershipAd`) || false;
  const partnershipOrderType =
    watch(`${basicPath}.partnershipOrderType`) ??
    ADS_CAMPAIGN_META_PARTNERSHIP_AD_ORDER_SELF_FIRST;
  const sponsorPageID = watch(`${basicPath}.sponsorPageID`) || "";
  const sponsorInstagramID = watch(`${basicPath}.sponsorInstagramID`) || "";

  const isAdWithSpecialConfigurationCustomizationPlacements =
    getIsAdWithSpecialConfigurationCustomizationPlacements({
      platform: META,
      destinationType: destinationTypeAdGroup,
      isCarousel,
      customizationPlacements,
    });

  const validateDataToGeneratePreview = () => {
    const destinationTypeIsInstagram =
      destinationTypeAdGroup ===
      ADS_CAMPAIGN_META_DESTINATION_TYPE_INSTAGRAM_PROFILE_OPTION;

    const destinationURL = destinationUrls?.[0]?.value;
    const isUrlValidDomain = verifyDestinationURLIsABlockedDomain({
      destinationURL,
      destinationType: destinationTypeAdGroup,
      platform: META,
    });

    if (!isUrlValidDomain.ok && !destinationTypeIsInstagram) {
      return isUrlValidDomain;
    }
    if (!adFormat) {
      return { ok: false, message: FIELD_AD_FORMAT_REQUIRED_AD_PREVIEW };
    }

    const validation = validationMetaAdPreview({
      objective,
      destinationTypeAdGroup,
      destinationTypeAd,
      callToAction,
      pageID,
      instagramID,
      appID,
      isCatalogAd,
      productSetID,
      isCarousel,
      isInstantExperience,
      isDynamicCreative,
      mediaData,
      mediaUrls,
      instantExperience,
      currentMediaData,
      destinationUrls,
      address,
      isPostAd,
      isPartnershipAd,
      partnershipOrderType,
      sponsorPageID,
    });
    if (!validation.ok) {
      return validation;
    }

    return { ok: true };
  };

  const onChangeAdPreview = (value) => {
    setAdPreviewSrc(value);
  };

  const onGetInformationIframe = () => {
    const { height, width, src } = formattedIframeByType({
      type: adFormat,
      iframe: adPreviewSrc || "",
      isMobile,
      isCarousel,
      platform: META,
    });

    return {
      height,
      width,
      src,
    };
  };

  const { height, width, src } = onGetInformationIframe();

  const getInitialData = async (type) => {
    const someMediaUrlIsBlob = mediaUrls?.some((mediaUrl) =>
      mediaUrl?.includes("blob:")
    );
    if (someMediaUrlIsBlob) {
      return;
    }

    if (!showAdPreview) {
      setIsLoadingIframe(true);
      return;
    }

    const isValidData = validateDataToGeneratePreview();
    if (!isValidData.ok) {
      setIsLoadingIframe(true);
      return setError(t(isValidData.message));
    }

    const formTitles = (getValues(`${basicPath}.titles`) || []).map(
      (title) => title?.value ?? ""
    );
    const formHeadlines = (getValues(`${basicPath}.headlines`) || []).map(
      (headline) => headline?.value ?? ""
    );
    const formDescriptions = (getValues(`${basicPath}.descriptions`) || []).map(
      (description) => description?.value ?? ""
    );

    const response = await dispatch(
      startGetAdPreview(
        {
          platform: META,
          campaignID,
          adID,
          mediaUrls,
          mediaData,
          mediaIDs,
          callToAction,
          callToActions: callToActionsFromForm,
          adPreview: adPreviewSrc || undefined,
          isDynamicContent:
            getValues(`${basicPathAdGroup}.isDynamicContent`) ??
            isDynamicCreative,
          ...(isDynamicCreative && {
            isDynamicCreative,
          }),
          titles: formTitles,
          headlines: formHeadlines,
          descriptions: formDescriptions,
          destinationUrls: destinationUrls
            ? destinationUrls.map((url) => url.value)
            : [],
          pageID,
          instagramID,
          useFBPageAsIG,
          adFormats: [adFormat],
          platformData,
          destinationType: destinationTypeAdGroup,
          destinationTypeAd,
          siteLinks,
          advantageEnhances: getValues(`${basicPath}.advantageEnhances`),
          isCarousel,
          isInstantExperience,
          instantExperience,
          productSetID,
          appID,
          isIOS14Campaign,
          address,
          isPostAd,
          isPartnershipAd,
          partnershipOrderType,
          sponsorPageID,
          sponsorInstagramID,
        },
        abortControllerRef.current.signal
      )
    );

    if (!response.ok) {
      if (response.aborted) {
        return;
      }
      setError(response.message);
    }

    if (response.ok) {
      const { adPreviews: adPreviewsResponse } = response.data;
      const newPreview = adPreviewsResponse[adFormat];

      let newObjectPreviewsCached = {};

      if (type === "values") {
        newObjectPreviewsCached[adFormat] = newPreview;
      } else {
        newObjectPreviewsCached = {
          ...previewsCached[id],
          [adFormat]: newPreview,
        };
      }

      onChangePreviewsCached({
        ...previewsCached,
        [id]: newObjectPreviewsCached,
      });

      onChangeAdPreview(newPreview);
    }
    setIsLoadingIframe(true);
  };

  useEffect(() => {
    if (formats.includes(adFormat)) return;
    setValueComponent("adFormat", formats[0]);

    // eslint-disable-next-line
  }, [isCarousel]);

  useEffect(() => {
    const formatsByConfiguration = getAvailableFormats({
      isCarousel,
      callToAction,
    });

    let baseFormats = _.cloneDeep(formatsByConfiguration);

    if (isAdWithSpecialConfigurationCustomizationPlacements) {
      baseFormats = baseFormats.filter(
        (option) =>
          !ADS_CAMPAIGN_META_AD_FORMATS_FILTERED_BY_SPECIAL_CONFIGURATION.includes(
            option
          )
      );
    }

    baseFormats = filterFormatsByManualPlacements({
      baseFormats,
      placements: groupPlacements,
    });

    if (
      isAdWithSpecialConfigurationCustomizationPlacements &&
      ADS_CAMPAIGN_META_AD_FORMATS_FILTERED_BY_SPECIAL_CONFIGURATION.includes(
        adFormat
      )
    ) {
      setValueComponent("adFormat", ADS_CAMPAIGN_AD_FORMAT_FACEBOOK_STORY);
    } else {
      setValueComponent("adFormat", baseFormats[0]);
    }

    setFormats(baseFormats);

    // eslint-disable-next-line
  }, [
    isCarousel,
    isAdWithSpecialConfigurationCustomizationPlacements,
    address,
    groupPlacements,
    callToAction,
  ]);

  useEffect(() => {
    if (!adFormat) return;

    if (abortControllerRef?.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setError(null);

    setIsLoadingIframe(true);

    // Use debounced function for values changes
    const timeoutId = setTimeout(() => {
      getInitialData("values");
    }, DELAY_DEBOUNCE * 2.5);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line
  }, [
    currentMediaUrl,
    currentMediaData?.postID,
    callToAction,
    pageID,
    instagramID,
    useFBPageAsIG,
    isPostAd,
    isPartnershipAd,
    partnershipOrderType,
    sponsorPageID,
    sponsorInstagramID,
    triggerGeneratePreview,
    destinationTypeAdGroup,
    destinationTypeAd,
    siteLinks,
    isCarousel,
    isInstantExperience,
    instantExperience,
    copyFieldsSignature,
  ]);

  useEffect(() => {
    if (!adFormat) return;

    if (abortControllerRef?.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setError(null);

    if (previewsCached?.[id]?.[adFormat]) {
      onChangeAdPreview(previewsCached[id][adFormat]);
      setIsLoadingIframe(true);
      return;
    }

    setAdPreviewSrc("");
    setIsLoadingIframe(true);

    // Use debounced function for adFormat changes
    const timeoutId = setTimeout(() => {
      getInitialData("adFormat");
    }, DELAY_DEBOUNCE * 2.5);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line
  }, [adFormat]);

  const previewRefreshTimeoutRef = useRef(null);

  const schedulePreviewRefresh = (type, delayMs = DELAY_DEBOUNCE * 2.5) => {
    if (!showAdPreview || !adFormat) {
      return;
    }

    if (previewRefreshTimeoutRef.current) {
      clearTimeout(previewRefreshTimeoutRef.current);
    }

    previewRefreshTimeoutRef.current = setTimeout(() => {
      previewRefreshTimeoutRef.current = null;
      getInitialData(type);
    }, delayMs);
  };

  const onTriggerGeneratePreview = (from = "") => {
    setIsLoadingIframe(true);
    if (from === "content" || from === "texts") {
      onChangePreviewsCached({
        ...previewsCached,
        [id]: {},
      });
    }
    setTriggerGeneratePreview((prev) => prev + 1);

    if (from === "texts") {
      schedulePreviewRefresh("texts", 0);
    } else if (from === "content") {
      schedulePreviewRefresh("values", DELAY_DEBOUNCE * 2.5);
    }
  };

  useImperativeHandle(ref, () => ({
    generatePreview: onTriggerGeneratePreview,
  }));

  const onChangeAdFormat = (direction) => {
    const currentIndex = formats.indexOf(adFormat);
    let nextIndex;
    if (direction === "next") {
      nextIndex = currentIndex + 1;
      if (nextIndex >= formats.length) {
        nextIndex = 0;
      }
    }
    if (direction === "back") {
      nextIndex = currentIndex - 1;
      if (nextIndex < 0) {
        nextIndex = formats.length - 1;
      }
    }

    setValueComponent("adFormat", formats[nextIndex]);
  };

  const commonSxContainer = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
    maxWidth: width,
    width: "100%",
  };

  if (showMediaUpload) {
    return (
      <Box sx={commonSxContainer}>
        {noHasPlatformData &&
          !isDisabledFields &&
          createElement(MetaContainerIntegration, {
            onChangeIntegration: ({ integrationID, adAccountID }) => {
              setValue(`platformsData.${META}.integrationID`, integrationID, {
                shouldValidate: true,
                shouldDirty: true,
              });
              setValue(`platformsData.${META}.adAccountID`, adAccountID, {
                shouldValidate: true,
                shouldDirty: true,
              });
            },
          })}
        <SelectNewContent
          isEngagementAndOnVideo={isEngagementAndOnVideo}
          userID={userID}
          adID={adID}
          basicPath={basicPath}
          basicPathAdGroup={basicPathAdGroup}
          control={control}
          mediaUrl={currentMediaUrl}
          currentMediaIndex={currentMediaIndex}
          onTriggerGeneratePreview={onTriggerGeneratePreview}
          onRegeneratingTextsChange={setIsRegeneratingTexts}
          platforms={platforms}
          platformData={platformData}
          websiteURL={websiteURL}
          language={language}
          errorMediaUrl={errorMediaUrl}
          isDisabledFields={isDisabledFields}
          showButton={!isCatalogAd && !isDynamicCreative}
          isVideo={isVideo}
        />
        <CardMedia
          url={currentMediaUrl}
          sx={{
            height,
            maxWidth: width,
            width: { xs: "100%", sm: width },
          }}
        />
        <AdPreviewRegeneratingTextsOverlay open={isRegeneratingTexts} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        maxWidth: width,
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <SelectAdPreviewFormat
          control={control}
          errors={errors}
          onChangeAdFormat={onChangeAdFormat}
          formats={formats}
          disabled={isPreviewFormatSelectDisabled}
        />
        <SelectNewContent
          isEngagementAndOnVideo={isEngagementAndOnVideo}
          userID={userID}
          adID={adID}
          basicPath={basicPath}
          basicPathAdGroup={basicPathAdGroup}
          control={control}
          mediaUrl={currentMediaUrl}
          currentMediaIndex={currentMediaIndex}
          onTriggerGeneratePreview={onTriggerGeneratePreview}
          onRegeneratingTextsChange={setIsRegeneratingTexts}
          platforms={platforms}
          platformData={platformData}
          websiteURL={websiteURL}
          language={language}
          errorMediaUrl={errorMediaUrl}
          isDisabledFields={isDisabledFields}
          showButton={!isCatalogAd && !isDynamicCreative}
          isVideo={isVideo}
        />
      </Box>
      {showAdPreview && (
        <>
          <AdMediaPreview
            isMobile={isMobile}
            isLoadingIframe={showIframePreviewLoader}
            src={src}
            width={width}
            height={height}
            error={error}
            onChangeIsLoadingIframe={setIsLoadingIframe}
            onTriggerGeneratePreview={onTriggerGeneratePreview}
          />
          {!error && !showIframePreviewLoader && (
            <Box
              sx={{
                alignSelf: "stretch",
                p: 1,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "warning.main",
              }}
            >
              <Typography
                component="p"
                variant="caption"
                sx={{
                  m: 0,
                  color: "warning.dark",
                  fontSize: "0.72rem",
                  lineHeight: 1.4,
                }}
              >
                {t(META_AD_PREVIEW_AD_BLOCKER_NOTICE)}
              </Typography>
            </Box>
          )}
        </>
      )}
      <AdPreviewRegeneratingTextsOverlay open={isRegeneratingTexts} />
    </Box>
  );
});

const AdMediaPreview = ({
  isMobile,
  isLoadingIframe,
  src,
  width,
  height,
  error,
  onChangeIsLoadingIframe,
  onTriggerGeneratePreview,
}) => {
  const { t } = useTranslationApp();
  return (
    <Box>
      {error && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Button
            type="button"
            onClick={() => onTriggerGeneratePreview()}
            startIcon={<RefreshIcon sx={{ fontSize: "1.5rem !important" }} />}
            color="secondary"
            sx={{
              width: "fit-content",
            }}
          >
            {t(BUTTON_RETRY)}
          </Button>
          <Typography
            variant="body2"
            color="error"
            align="center"
            sx={{
              "& *": {
                p: "revert",
                m: "revert",
                listStyle: "revert",
                textDecoration: "revert",
              },
            }}
            dangerouslySetInnerHTML={{
              __html: error,
            }}
          />
        </Box>
      )}
      {!error && (
        <Box
          sx={{
            maxWidth: width,
            width: "100%",
            height,
            position: "relative",
          }}
        >
          {isLoadingIframe && (
            <>
              <Skeleton
                variant="rectangular"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  maxWidth: width,
                  width: "100%",
                  height,
                  zIndex: 3,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(255, 255, 255, 0.5)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 4,
                }}
              >
                <CircularProgress size={40} />
              </Box>
            </>
          )}

          {src && (
            <iframe
              src={src}
              onLoad={() => {
                onChangeIsLoadingIframe(false);
              }}
              scrolling="no"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                border: isMobile ? "none" : "1px solid white",
                overflow: "hidden",
                maxWidth: width,
                width: "100%",
                height,
                zIndex: 2,
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

const SelectNewContent = ({
  isEngagementAndOnVideo,
  basicPath,
  basicPathAdGroup,
  control,
  userID,
  adID,
  currentMediaIndex,
  onTriggerGeneratePreview,
  onRegeneratingTextsChange = () => {},
  videoData: videoDataProp,
  mediaUrl,
  platformData,
  websiteURL,
  language,
  errorMediaUrl,
  isDisabledFields,
  showButton = true,
  isVideo,
}) => {
  const { t } = useTranslationApp();
  const [isOpenImageEditModal, setIsOpenImageEditModal] = useState(false);
  const {
    setValue,
    watch,
    getValues,
    trigger,
    creativeOptimizations,
    onRemoveCreativeOptimization,
  } = useFormContext();
  const dispatch = useDispatchApp();

  const creativeEntry = getMatchedCreativeEntryForAdSlot({
    creativeOptimizations,
    basicPath,
  });

  const socialNetworkURL = watch("socialNetworkURL");
  const destinationType = watch(`${basicPathAdGroup}.destinationType`);
  const optimizationGoal = watch(`${basicPathAdGroup}.optimizationGoal`);
  const isPostAd = watch(`${basicPath}.isPostAd`);
  const reuseExistingCreative = watch(`${basicPath}.reuseExistingCreative`);
  // Objective lives under platformsData.meta (same as parent AdPreviewMeta), not root "objective".
  const objective = platformData?.objective;
  const productContext = watch("productContext");
  const targetContext = watch("targetContext");
  const ageRange = watch(`${basicPathAdGroup}.ageRange`);
  const genders = watch(`${basicPathAdGroup}.genders`);
  const isCarousel = watch(`${basicPath}.isCarousel`);
  const platformsData = watch("platformsData") || {};
  const mediaUrlsAll = watch(`${basicPath}.mediaUrls`) || [];
  const destinationTypeAd = watch(`${basicPath}.destinationType`);

  const isOnPostDestination = getIsOnPostDestinationMeta({
    destinationType,
    isPostAd,
  });

  const isCarouselAdsAvailable = getIsCarouselAdIsAvailable({
    platform: META,
    destinationType,
    isPostAd,
    optimizationGoal,
  });

  const onChangeContent = async (content) => {
    if (
      content?.type === "reuseCreative" ||
      content?.from === "reuseCreative"
    ) {
      applyMetaReusableCreativeToAd({
        setValue,
        basicPath,
        creative: content,
      });
      onTriggerGeneratePreview?.();
      return;
    }

    await changeCreativeWithOptionalTextRegeneration({
      platform: META,
      content,
      basicPath,
      adID,
      currentMediaIndex,
      isCarousel,
      dispatch,
      getValues,
      setValue,
      watch,
      trigger,
      t,
      language,
      campaignContext: watch("context") || "",
      creativeOptimizations,
      onRemoveCreativeOptimization,
      onTriggerGeneratePreview,
      onRegeneratingTextsChange,
      skipRegenerateTexts: isOnPostDestination,
    });
  };

  const onCloseImageEditModal = () => {
    setIsOpenImageEditModal(false);
  };

  const onSaveImageEditContent = async (savePayload) => {
    const editedUrl =
      typeof savePayload === "string" ? savePayload : savePayload?.url;

    if (!editedUrl) return;

    await onChangeContent({
      url: editedUrl,
      type: "upload",
    });
    onCloseImageEditModal();
  };

  useEffect(() => {
    if (!creativeEntry?.title) return;
    const element = document.getElementById(
      `${basicPath}.mediaUrls.[${currentMediaIndex}]`
    );
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    // eslint-disable-next-line
  }, [creativeEntry?.slotKey, basicPath, currentMediaIndex]);

  if (!showButton) return;

  const FORMATS_MEDIA_OBJECTIVE =
    isEngagementAndOnVideo ||
    optimizationGoal === ADS_CAMPAIGN_META_OPTIMIZATION_GOAL_THRUPLAY
      ? VIDEO_FORMATS
      : FORMATS_MEDIA_META;

  const showTooltip = !_.isEmpty(creativeEntry);

  return (
    <>
      <TooltipOptimization
        isOpen={showTooltip}
        titleTooltip={creativeEntry?.title}
        actionTooltip={creativeEntry?.action}
      >
        <Box
          id={`${basicPath}.mediaUrls.[${currentMediaIndex}]`}
          data-field={`${basicPath}.mediaUrls.[${currentMediaIndex}]`}
          sx={{
            width: "100%",
          }}
        >
          <Controller
            name={`${basicPath}.mediaUrls.[${currentMediaIndex}]`}
            control={control}
            rules={{
              required: {
                value: true,
                message: t(FIELD_REQUIRED),
              },
              validate: (value) => {
                if (value?.includes("blob:")) {
                  return t(FIELD_CONTENT_IS_BEING_PROCESSED);
                }
              },
            }}
            render={() => {
              const showEditContentButton =
                !isOnPostDestination &&
                !isCarousel &&
                Boolean(mediaUrl) &&
                !isVideo &&
                !reuseExistingCreative;

              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    width: "100%",
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: showEditContentButton ? "50%" : "100%",
                    }}
                  >
                    <IconButtonAddContent
                      id={`add-content-media-${basicPath}`}
                      mode="button"
                      disabled={isDisabledFields}
                      minDimensions={MIN_DIMENSIONS_CONTENT}
                      showButtonUpload={true}
                      pathStorage={`adsCampaigns/${userID}/`}
                      modeUpload="sync"
                      onAddContent={(contents) => {
                        const selectedContent = contents?.[0];
                        if (!selectedContent) return;
                        onChangeContent(selectedContent);
                      }}
                      initialValues={{
                        language,
                        platform: META,
                        ...(creativeEntry
                          ? {
                              prompt:
                                buildCreativeOptimizationPrompt(creativeEntry),
                            }
                          : {}),
                      }}
                      onCallbackSaveCanva={(mediaUrlCanva) => {
                        const canvaContent = {
                          url: mediaUrlCanva,
                          type: "upload",
                        };
                        onChangeContent(canvaContent);
                      }}
                      multiple={false}
                      acceptedFormats={FORMATS_MEDIA_OBJECTIVE.concat(
                        isCarouselAdsAvailable
                          ? [ADS_CAMPAIGN_CAROUSEL_FORMAT]
                          : []
                      )}
                      urlContent={mediaUrl}
                      textButton={
                        mediaUrl
                          ? BUTTON_REPLACE_CONTENT
                          : isOnPostDestination
                            ? BUTTON_SELECT_POST
                            : BUTTON_SELECT_CONTENT
                      }
                      showGenerateWithAIOption={
                        !isOnPostDestination && !reuseExistingCreative
                      }
                      showUploadDeviceOption={
                        !isOnPostDestination && !reuseExistingCreative
                      }
                      showGalleryOption={
                        !reuseExistingCreative && !isOnPostDestination
                      }
                      forcePostSelection={isOnPostDestination}
                      forceReuseSelection={Boolean(reuseExistingCreative)}
                      propsReuseCreatives={{
                        advertiserID: userID,
                        adAccountID: platformData?.adAccountID,
                        pageID: platformData?.pageID,
                        integrationID: platformData?.integrationID,
                        mediaUrls: mediaUrlsAll,
                        isCarousel,
                        objective,
                        destinationType,
                        destinationTypeAd,
                        optimizationGoal,
                      }}
                      destinationType={destinationType}
                      propsPostsAdsPlatforms={platformsData}
                      propsGenerateAI={{
                        videoData: videoDataProp?.videoID
                          ? { ...videoDataProp, id: videoDataProp?.videoID }
                          : null,
                        websiteURL,
                        socialNetworkURL,
                        language,
                        integrationID: platformData?.integrationID,
                        adAccountID: platformData?.adAccountID,
                        campaignContext: {
                          objective,
                          productContext,
                          targetContext,
                          genders,
                          ageRange,
                        },
                      }}
                      color="secondary"
                      sx={{
                        gap: 1,
                        alignItems: "flex-start",
                        width: "100%",
                        mb: 0,
                      }}
                    />
                  </Box>
                  {showEditContentButton && (
                    <Button
                      color="secondary"
                      disabled={isDisabledFields}
                      onClick={() => setIsOpenImageEditModal(true)}
                      sx={{
                        whiteSpace: "nowrap",
                        width: "50%",
                      }}
                    >
                      {t(BUTTON_EDIT_CONTENT)}
                    </Button>
                  )}
                </Box>
              );
            }}
          />
          {errorMediaUrl && (
            <Typography
              color="error"
              variant="caption"
              dangerouslySetInnerHTML={{
                __html: errorMediaUrl?.message,
              }}
              sx={{
                "& *": {
                  p: "revert",
                  m: "revert",
                  listStyle: "revert",
                  textDecoration: "revert",
                },
              }}
            />
          )}
        </Box>
      </TooltipOptimization>
      {isOpenImageEditModal && (
        <ModalImageEdit
          isOpen={isOpenImageEditModal}
          onClose={onCloseImageEditModal}
          initialImageUrl={mediaUrl}
          onCallbackSave={onSaveImageEditContent}
          showSaveAlert={false}
        />
      )}
    </>
  );
};

const SelectAdPreviewFormat = ({
  control,
  errors,
  formats,
  disabled = false,
}) => {
  const { t } = useTranslationApp();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
      }}
    >
      <Select
        variant="filled"
        name="adFormat"
        label={t(FORMAT_PLACEHOLDER)}
        options={formats.map((format) => ({
          value: format,
          label: t(format),
        }))}
        control={control}
        errors={errors}
        disabled={disabled}
        fullWidth={true}
        sx={{
          mb: 1,
        }}
      />
    </Box>
  );
};

AdPreviewMeta.displayName = "AdPreviewMeta";

export default AdPreviewMeta;
